import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * The single KPI card the assessment's backlog #8 asks for — Dashboard.tsx
 * had its own local `StatCard` (icon+value+label+sub, border-left accent);
 * `Card.tsx`'s `StatCard` shell was independently composed per-instance on
 * QueueView.tsx and ReportsModule.tsx with no shared internal layout. All
 * three agreed on the same rough shape (accent stripe, big value, small
 * label) but disagreed on every detail — icon placement, sub-caption
 * styling, accent side. One primitive now owns that layout; `Card.tsx`'s
 * bare `StatCard` stays as-is for a caller that genuinely wants to compose
 * its own content inside the shell (nothing here forces every accented
 * card in the app through this component).
 *
 * `delta`/`trend` are optional slots for period-over-period comparison —
 * deliberately unpopulated everywhere this lands today. Backlog #11 (period
 * deltas) needs its own small backend addition (`GET
 * /api/reports/daily-summary` gaining a prior-window comparison) before any
 * caller can pass a real value; the assessment is explicit that a client-side
 * delta must never be faked. The slot exists now so #11 is a data change,
 * not another round of touching every KPI card in the app.
 */
export interface KpiDelta {
  /** Signed percentage vs. the prior period, e.g. 12 or -8. */
  value: number;
  /** What the comparison window is, e.g. "vs yesterday". */
  label?: string;
  /**
   * Which direction is good news, so the colour says "better/worse" rather
   * than "up/down": `'up'` (default — registrations, consults), `'down'`
   * (patients waiting: fewer is better), `'neutral'` (always grey). 0% is
   * always grey.
   */
  goodWhen?: 'up' | 'down' | 'neutral';
}

export function deltaToneClass(delta: KpiDelta): string {
  if (delta.value === 0 || delta.goodWhen === 'neutral') return 'text-ink-muted';
  const good = (delta.goodWhen ?? 'up') === 'up' ? delta.value > 0 : delta.value < 0;
  return good ? 'text-success-ink' : 'text-danger-ink';
}

/** The categorical tones from theme.css — identity, never status. */
export type KpiTone = 'blue' | 'orange' | 'violet' | 'green' | 'red' | 'teal' | 'indigo' | 'slate';

// Literal class strings (not built from the tone name) so Tailwind's
// scanner sees every one of them.
const TONE_BADGE: Record<KpiTone, string> = {
  blue: 'bg-tone-blue-soft text-tone-blue', orange: 'bg-tone-orange-soft text-tone-orange',
  violet: 'bg-tone-violet-soft text-tone-violet', green: 'bg-tone-green-soft text-tone-green',
  red: 'bg-tone-red-soft text-tone-red', teal: 'bg-tone-teal-soft text-tone-teal',
  indigo: 'bg-tone-indigo-soft text-tone-indigo', slate: 'bg-tone-slate-soft text-tone-slate',
};
const TONE_TEXT: Record<KpiTone, string> = {
  blue: 'text-tone-blue', orange: 'text-tone-orange', violet: 'text-tone-violet', green: 'text-tone-green',
  red: 'text-tone-red', teal: 'text-tone-teal', indigo: 'text-tone-indigo', slate: 'text-tone-slate',
};
export const toneBadgeClass = (tone: KpiTone) => TONE_BADGE[tone];
export const toneTextClass = (tone: KpiTone) => TONE_TEXT[tone];

export interface KpiTileProps {
  /**
   * A lucide icon element (e.g. `<Users className="h-4 w-4" />`), not an
   * emoji string — per a client-supplied mockup review (11 Sep 2026), this
   * card's icon is chrome (a category marker for the stat, not a clinical
   * domain marker), which is exactly the case the assessment's own icon
   * policy (backlog #14) already carves out for lucide over emoji. Kept
   * generic (`ReactNode`) rather than a lucide-specific type so a caller can
   * still pass anything else if it ever needs to.
   */
  icon: ReactNode;
  /**
   * The icon's circular badge background + text colour together, e.g.
   * `"bg-info-soft text-info-ink"` — a literal Tailwind utility pair, not a
   * `style` prop (see `accentClassName`'s own note on why). Defaults to a
   * neutral tint if omitted.
   */
  iconClassName?: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  /**
   * A literal Tailwind border-left utility, e.g. `"border-l-[3px] border-l-warn"`
   * — not a `style` prop. `components/ui/**` is lint-gated against new inline
   * styles and hardcoded hex literals (backlog item 16); an accent that
   * `Card.tsx`'s `StatCard.accentColor` would express as a raw hex string is
   * written here as the equivalent Tailwind class instead (a literal string
   * in the caller's own source, so Tailwind's JIT scanner can see it — the
   * same pattern `DataTable.tsx`'s `widthClassName` already established).
   */
  accentClassName?: string;
  delta?: KpiDelta;
  /** Free-form trend slot (e.g. a sparkline) — not wired to any data source yet, see the header comment. */
  trend?: ReactNode;
  onClick?: () => void;
  className?: string;
  /**
   * `'md'` (default) is every existing caller's look, unchanged. `'sm'` is a
   * denser variant — smaller icon badge, value and label — for a screen
   * packing more tiles into one row than `'md'`'s sizing comfortably fits
   * (Claims Desk's 6-up KPI row at laptop widths, where "Final auth
   * awaiting" was clipping under the default size).
   */
  size?: 'md' | 'sm';
  /**
   * `'inline'` (default) is every existing caller's layout. `'stacked'` is the
   * 2026-10 dashboard layout (docs/design/dashboard): square tinted icon
   * badge, value coloured by `tone`, label + sub beside it, and a divided
   * footer row holding `footer` (or the delta, or nothing). No accent stripe.
   */
  layout?: 'inline' | 'stacked';
  /** Stacked only: categorical tone for the icon badge and the value. */
  tone?: KpiTone;
  /** Stacked only: footer content (text or an action). Wins over the delta. */
  footer?: ReactNode;
}

export function KpiTile({ icon, iconClassName, label, value, sub, accentClassName, delta, trend, onClick, className, size = 'md', layout = 'inline', tone = 'slate', footer }: KpiTileProps) {
  const sm = size === 'sm';
  const Tag = onClick ? 'button' : 'div';
  if (layout === 'stacked') {
    const foot = footer ?? (delta ? (
      <span>
        <span className={cn('font-semibold', deltaToneClass(delta))}>
          {delta.value > 0 ? '↑' : delta.value < 0 ? '↓' : '–'} {Math.abs(delta.value)}%
        </span>
        {delta.label ? ` ${delta.label}` : ''}
      </span>
    ) : null);
    return (
      <Tag
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        className={cn(
          'flex flex-col rounded-[var(--radius)] border border-line bg-card px-4 pt-[18px] text-left shadow-card',
          onClick && 'transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25',
          className
        )}
      >
        <div className="flex items-start gap-[18px]">
          <div className={cn('flex h-[47px] w-[47px] shrink-0 items-center justify-center rounded-[9px]', iconClassName ?? toneBadgeClass(tone))}>
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className={cn('mt-0.5 text-[27px] font-bold leading-none tracking-tight', toneTextClass(tone))}>{value}</div>
            <div className="mt-3 truncate text-[12.5px] font-semibold text-ink">{label}</div>
            {sub && <div className="mt-[3px] truncate text-[11.5px] text-ink-muted">{sub}</div>}
          </div>
        </div>
        <div className="mt-auto flex min-h-[38px] items-center justify-center pb-1 text-[11.5px] text-ink-muted">
          {foot}
        </div>
      </Tag>
    );
  }
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'rounded-xl border border-border bg-card text-left shadow-card',
        sm ? 'px-3 py-2' : 'px-4 py-3',
        accentClassName,
        onClick && 'transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25',
        className
      )}
    >
      {/* Icon sits beside the value instead of on its own row above it — the
          value/label/sub stack this shares a row with is what used to be a
          separate row below the icon, and dropping that row is what actually
          shortens the card; a smaller icon badge alone wouldn't. */}
      <div className={cn('flex items-center', sm ? 'gap-1.5' : 'gap-2.5')}>
        <div className={cn('flex shrink-0 items-center justify-center rounded-full', sm ? 'h-7 w-7' : 'h-9 w-9', iconClassName ?? 'bg-muted text-ink-muted')}>
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <div className={cn('font-bold leading-none tracking-tight', sm ? 'text-[16px]' : 'text-[22px]')}>{value}</div>
            {delta && (
              <span className={cn('font-semibold', sm ? 'text-[10.5px]' : 'text-[12px]', deltaToneClass(delta))}>
                {delta.value > 0 ? '↑' : delta.value < 0 ? '↓' : '–'} {Math.abs(delta.value)}%
              </span>
            )}
          </div>
          <div className={cn('mt-0.5 font-semibold text-ink', sm ? 'text-[11px] leading-tight' : 'truncate text-[12.5px]')}>{label}</div>
        </div>
      </div>
      {sub && <div className={cn('mt-1.5 text-ink-muted', sm ? 'truncate text-[10px]' : 'text-[11px]')}>{sub}{delta?.label ? ` · ${delta.label}` : ''}</div>}
      {trend}
    </Tag>
  );
}
